import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  ElementRef,
  OnInit,
  OnDestroy,
  HostListener,
  ViewContainerRef,
  NgZone,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

export interface IAdvancedDropdownItem {
  id: number;
  name: string;
  owner: string;
  organizationName: string;
  type: 'Public' | 'Private';
  original: any;
}

export enum SortDirections {
  ASC = 'asc',
  DESC = 'desc',
}

@Component({
  selector: 'app-advanced-dropdown',
  templateUrl: './advanced-dropdown.component.html',
  styleUrls: ['./advanced-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedDropdownComponent implements OnChanges, OnInit, OnDestroy {
  readonly minCharsToSearch: number = 3;
  
  // ===== ViewChild =====
  @ViewChild('dropdownTemplate') dropdownTemplate: TemplateRef<any>;
  @ViewChild('trigger') trigger: ElementRef;

  // ===== Inputs =====
  @Input() items: any[];
  @Input() placeholder: string = '';
  @Input() showClearButton: boolean = true;
  @Input() popupWidth: number = 700;
  @Input() showFooter: boolean = false;
  @Input() translationPrefix: string = '';
  @Input() mapItemToDropdownItem: (item: any) => IAdvancedDropdownItem;
  @Input() columns: Array<{
    field: string;
    header: string;
    width?: string;
    cellTemplate?: TemplateRef<any>;
  }>;
  @Input() disableCreateButton: boolean = false;
  @Input() disableSaveButton: boolean = false;

  private _selectedId: number | null = null;
  sortDirection: SortDirections = SortDirections.ASC;
  sortField: string;
  public get selectedId(): number | null {
    if(this._selectedId === -1) {
      return null;
    }
    return this._selectedId;
  }
  @Input()
  public set selectedId(value: number | null) {
    this._selectedId = value;
  }

  // ===== Outputs =====
  @Output() itemSelected = new EventEmitter<IAdvancedDropdownItem>();
  @Output() createClicked = new EventEmitter<void>();
  @Output() saveClicked = new EventEmitter<IAdvancedDropdownItem>();
  @Output() beforeOpen = new EventEmitter<any>();

  // ===== Public Properties =====
  private _searchText = '';
  protected isLoading = true;
  private overlayRef: OverlayRef;
  private destroy$ = new Subject<void>();

  public get searchText(): string {
    return this._searchText;
  }
  public set searchText(value: string) {
    this._searchText = value;
  }

  public get totalCount(): number {
    return this.items?.length || 0;
  }

  public get enableVirtualization(): boolean {
    const minItemsRequiredBeforeVirtualization = 20;
    return this.dropdownItems && this.dropdownItems.length > minItemsRequiredBeforeVirtualization;
  }

  private _loading: boolean = false;
  public get loading(): boolean {
    return this._loading;
  }
  public set loading(value: boolean) {
    this._loading = value;
    // Update panel class if overlay exists
    if (this.overlayRef) {
      if (value) {
        this.overlayRef.addPanelClass('loading');
      } else {
        this.overlayRef.removePanelClass('loading');
      }
    }
    this._cdr.detectChanges();
  }
  
  public dropdownFields = { text: 'name', value: 'id' };
  public dropdownItems: IAdvancedDropdownItem[] = null;
  public isOpen: boolean = false;
  public filteredItems: IAdvancedDropdownItem[] = [];

  public get filterCount(): number {
    return this.filteredItems?.length || 0;
  }

  public get selectedText(): string {
    const selectedItem = this.dropdownItems?.find(item => item.id === this.selectedId);
    return selectedItem ? selectedItem.name : '';
  }

  constructor(
    private _translateService: TranslateService,
    private _cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private ngZone: NgZone
  ) {}

  // ===== Lifecycle Hooks =====
  ngOnInit(): void {
    this.filteredItems = this.dropdownItems || [];
    
    // Listen for scroll events on window and update overlay position
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'scroll', { capture: true })
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(10)  // Limit updates for performance
        )
        .subscribe(() => {
          if (this.isOpen && this.overlayRef) {
            this.overlayRef.updatePosition();
          }
        });
      
      // Also listen for resize events
      fromEvent(window, 'resize')
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(50)
        )
        .subscribe(() => {
          if (this.isOpen && this.overlayRef) {
            this.overlayRef.updatePosition();
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeDropdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['items'] &&
      this.items &&
      !isEqual(changes['items'].currentValue, changes['items'].previousValue)
    ) {
      this.processItems();
      this.filterItems();
      if(this._selectedId !== null) {
        this.isLoading = false;
      }
    }

    if (changes['selectedId'] && changes['selectedId'].currentValue && this._selectedId !== null && this.dropdownItems) {
      this.isLoading = false;
    }

  }

  // ===== Public Methods - Event Handlers =====
  onFiltering(searchText: string): void {
    this.searchText = searchText;
    this.filterItems();
  }

  onItemSelect(item: IAdvancedDropdownItem): void {
    this.selectedId = item.id;
    this.itemSelected.emit(item);
    this.closeDropdown();
  }

  onCreateClick(): void {
    this.closeDropdown();
    this.createClicked.emit();
  }

  onSaveClick(): void {
    const selectedItem = this.dropdownItems.find(item => item.id === this.selectedId);
    if (selectedItem) {
      this.closeDropdown();
      this.saveClicked.emit(selectedItem);
    }
  }

  onOpen($event: any) {
    this.isOpen = true;
  }

  onClose($event: any) {
    this.isOpen = false;
    this.searchText = '';
  }

  headerCellClicked(field: string): void {
    this.sortDirection = this.sortDirection === SortDirections.ASC ? SortDirections.DESC : SortDirections.ASC;
    this.sortField = field;
    this.sortByColumn(field, this.sortDirection);
  }

  sortByColumn(field: any, sortDirection: any) {
    this.sortField = field;
    this.sortDirection = sortDirection;
    this.filteredItems = this.filteredItems.sort((a, b) => {
      if (sortDirection === SortDirections.ASC) {
        return a[field].localeCompare(b[field]);
      } else {
        return b[field].localeCompare(a[field]);
      }
    });
    this._cdr.detectChanges();
  }

  clearSelection(event?: MouseEvent): void {
    if (event) {
      // Stop event propagation to prevent dropdown toggle
      event.stopPropagation();
      event.preventDefault();
    }
    
    this.selectedId = null;
    this.itemSelected.emit(null);
  }

  public openDropDown() {
    this.openDropdown();
  }

  public closeDropDown() {
    this.closeDropdown();
  }

  public toggleDropDown() {
    // Only allow toggling from the trigger element
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  public refreshDataAndOpen(): void {
    setTimeout(() => {
      this.filterItems();
      setTimeout(() => this.openDropdown(), 100);
      this.isOpen = true;
    }, 100);
  }

  public refresh() {
    this.filterItems();
    this._cdr.detectChanges();
  }
  
  public getFilterResultsMessage(): string {
    if (this.searchText && this.searchText.length > 0) {
      return this._translateService
        .instant(`${this.translationPrefix}.FILTER_RESULTS_FOUND`)
        .replace('{count}', this.filterCount.toString())
        .replace('{total}', this.totalCount.toString());
    }
    return this._translateService
      .instant(`${this.translationPrefix}.FILTER_RESULTS_TOTAL`)
      .replace('{total}', this.totalCount.toString());
  }

  // We're now using overlay backdrop for outside clicks
  // This is kept as a fallback for edge cases
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Skip this handler if we're not open or if we're using the backdrop approach
    if (!this.isOpen || !this.overlayRef) {
      return;
    }
    
    // Skip if the click is on the dropdown itself
    if (this.overlayRef.overlayElement.contains(event.target as Node)) {
      return;
    }
    
    // Skip if the click is on the trigger element (we handle toggle separately)
    if (this.elementRef.nativeElement.contains(event.target as Node)) {
      return;
    }
    
    // Close if clicking outside both the trigger and overlay
    // This is a backup mechanism as the backdrop should handle this
    this.ngZone.run(() => {
      this.closeDropdown();
    });
  }

  private openDropdown(): void {
    if (this.isOpen || !this.dropdownTemplate) {
      return;
    }

    this.beforeOpen.emit({});

    // Get the trigger element height
    const triggerElement = this.trigger.nativeElement;

    // Create a position strategy that will update on scroll
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.trigger)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4, // Add trigger height plus small gap
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: - 4, // Subtract trigger height plus small gap
        }
      ])
      .withPush(true)
      .withScrollableContainers([])
      .withFlexibleDimensions(false);

    // Use a reposition scroll strategy to update position on scroll and 
    // enable a transparent backdrop to detect outside clicks
    this.overlayRef = this.overlay.create({
      positionStrategy,
      width: this.popupWidth,
      maxHeight: 500,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true, // Enable backdrop for click detection
      backdropClass: 'cdk-overlay-transparent-backdrop', // Transparent backdrop
      panelClass: this.loading ? ['custom-dropdown-panel', 'loading'] : 'custom-dropdown-panel',
    });

    // Listen to backdrop clicks
    this.overlayRef.backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeDropdown();
      });

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    // Update the position when scrolling
    this.overlayRef.updatePosition();
    
    // Listen for detachments
    this.overlayRef.detachments()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isOpen = false;
        this.onClose({});
      });

    this.isOpen = true;
    this.onOpen({});
    this._cdr.detectChanges();
  }

  private closeDropdown(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isOpen = false;
    this.onClose({});
    this._cdr.detectChanges();
  }

  private filterItems(): void {
    if (!this.dropdownItems) {
      this.filteredItems = [];
      return;
    }

    if (!this.searchText || this.searchText.length < this.minCharsToSearch) {
      this.filteredItems = [...this.dropdownItems];
    } else {
      const searchLower = this.searchText.toLowerCase();
      this.filteredItems = this.dropdownItems.filter(item => 
        item.name?.toLowerCase().includes(searchLower) ||
        item.owner?.toLowerCase().includes(searchLower) ||
        item.organizationName?.toLowerCase().includes(searchLower) ||
        item.type?.toLowerCase().includes(searchLower)
      );
    }
    this._cdr.detectChanges();
  }

  public get filteredSearchText(): string {
    return this._searchText?.length >= this.minCharsToSearch ? this._searchText : '';
  }

  private processItems() {
    this.dropdownItems = this.items.map(item => this.mapItemToDropdownItem(item));
    this.filteredItems = [...this.dropdownItems];
  }
}
